import { Injectable } from '@angular/core';
import { Printer, PrintOptions } from '@awesome-cordova-plugins/printer/ngx';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { base64ArrayBuffer } from '@common/base64/base64';
import { NativeShareService, ShareOptions } from '@services/share/native-share.service';

const PDF_MIME_TYPE = 'application/pdf';
export function toSaveFileName(fileName: string): string {
  const safeName = (fileName ?? '')
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_/, '')
    .replace(/_$/, '');
  return `${safeName || 'document'}.pdf`;
}

/**
 * Printing, downloading and sharing of pdf documents that already exist as
 * binary data
 */
@Injectable({ providedIn: 'root' })
export class PdfDocumentService {
  constructor(
    private _printer: Printer,
    private _nativeShareService: NativeShareService
  ) {}

  /**
   * offer native print functions for a file
   *
   * @param pdf file to print
   * @param fileName file name
   * @param printOptions
   */
  public async print(pdf: ArrayBuffer, fileName: string, printOptions: PrintOptions = {}): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      const fileUri = await this.writeToCache(pdf, fileName);
      await this._printer.print(fileUri, { name: fileName, ...printOptions });
      // a cache file that cannot be removed must not fail the print itself
      await Filesystem.deleteFile({ path: fileUri }).catch(error =>
        window.logger.info(`Could not remove the printed pdf from the cache: ${error?.message}`)
      );
      return;
    }
    this.printBlob(new Blob([pdf], { type: PDF_MIME_TYPE }));
  }

  /**
   * print a file through the browser
   *
   * @param blob file to print
   */
  public printBlob(blob: Blob): void {
    this.printBlobUrl(URL.createObjectURL(blob));
  }

  /**
   * print a file through the browser from an existing object url
   *
   * @param blobUrl object url of the file to print
   */
  public printBlobUrl(blobUrl: string): void {
    const iframe = document.createElement('iframe');
    window.onafterprint = () => {
      document.body.removeChild(iframe);
    };
    document.body.appendChild(iframe);
    iframe.style.display = 'none';
    iframe.onload = () => {
      setTimeout(() => {
        iframe.focus();
        iframe.contentWindow.print();
      }, 1);
    };
    iframe.src = blobUrl;

    const cleanup = () => {
      iframe.parentElement?.removeChild(iframe);
      window.removeEventListener('focus', cleanup);
    };
    window.addEventListener('focus', cleanup, { once: true });
  }

  /**
   * download a provided file (browser/web only)
   *
   * @param pdf file to download
   * @param fileName file name
   */
  public download(pdf: ArrayBuffer, fileName: string): void {
    const blobUrl = URL.createObjectURL(new Blob([pdf], { type: PDF_MIME_TYPE }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${fileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }

  /** Hands the document to the native share sheet, native platforms only. */
  /**
   * share a file with the native share dialog (cordova only)
   * @param pdf file to share
   * @param fileName name of the file
   */
  public async share(pdf: ArrayBuffer, fileName: string): Promise<boolean> {
    const options: ShareOptions = {
      title: fileName,
      text: fileName,
      dialogTitle: fileName,
      files: [{ filename: `${fileName}.pdf`, base64: base64ArrayBuffer(pdf) }],
    };
    return this._nativeShareService.share(options);
  }

  private async writeToCache(pdf: ArrayBuffer, fileName: string): Promise<string> {
    const file = await Filesystem.writeFile({
      directory: Directory.Cache,
      path: toSaveFileName(fileName),
      data: base64ArrayBuffer(pdf),
    });
    return file.uri;
  }
}
